import { c as _c } from "react/compiler-runtime";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router";
import { OPERATOR } from "@/lib/operator";
import { cn } from "@/lib/utils";
import { Logo } from "./logo";
import { PaymentBadges } from "./payment-badges";

// B2C legal documents. Served as static PDFs from the same origin under
// `/docs/b2c/…` (see `src/index.ts`). Filenames are Cyrillic with spaces, so
// every href is run through `encodeURI`. Порядок — как в макете «Подвал»:
// список идёт по колонкам сверху вниз (3 + 3 + 2 на десктопе, 4 + 4 на планшете
// и телефоне), поэтому сетка ниже заполняется по колонкам (grid-flow-col).
const LEGAL_DOCS: {
  file: string;
  label: string;
}[] = [{
  file: "Политика обработки персональных данных OSMIX.pdf",
  label: "Политика обработки персональных данных"
}, {
  file: "Согласие на обработку персональных данных.pdf",
  label: "Согласие на обработку персональных данных"
}, {
  file: "Политика конфиденциальности OSMIX.pdf",
  label: "Политика конфиденциальности"
}, {
  file: "Пользовательское соглашение OSMIX.pdf",
  label: "Пользовательское соглашение (оферта)"
}, {
  file: "Правила доставки и примерки OSMIX.pdf",
  label: "Правила доставки и\u00A0примерки"
}, {
  file: "Правила возврата и обмена товаров OSMIX.pdf",
  label: "Правила возврата и\u00A0обмена товаров"
}, {
  file: "Правила оплаты юридическими лицами OSMIX.pdf",
  label: "Правила оплаты юридическими лицами"
}, {
  file: "Политика использования cookie OSMIX.pdf",
  label: "Политика использования cookie"
}];

/*
 * Подвал по кадрам «Подвал» (Figma 3954:114420 — 1024, 3954:114450 — 744,
 * 3954:114510 — 320, подвал в 2871:30392 — 1200). Три схемы:
 *   • телефон (< 744): всё в столбик — бренд, «Покупателям | Поддержка» в ряд,
 *     ниже «Правовая информация» в две колонки (Айталы A-01: последние три
 *     колонки уходят вниз); Manrope 10/13;
 *   • планшет (744–1023): бренд слева, навигация справа, правовая — 2 колонки;
 *     Manrope 10/13;
 *   • десктоп (≥ 1024): то же, правовая — 3 колонки; Manrope 12/16.
 * Все колонки выровнены по верхнему краю (Айталы A-01, критично): заголовки
 * колонок — одной строкой одной высоты, списки начинаются на одной линии.
 * Цвет текста #2E2E2E, заголовки w700 ls −0,31, ссылки w400; ссылка текущей
 * страницы — #FA5C2E w500 с подчёркиванием (в макете так выделена «Лента»).
 */
const TEXT = "text-[10px] leading-[13px] lg:text-[12px] lg:leading-4 text-[#2E2E2E]";
const HEADING = cn(TEXT, "font-bold tracking-[-0.31px]");
const LINK = cn(TEXT, "font-normal transition-colors hover:text-[#FA5C2E]");
// Активная ссылка: подчёркивание на 3/6 px ниже базовой линии, как в макете.
const ACTIVE = "font-medium text-[#FA5C2E] underline decoration-1 underline-offset-[3px] lg:underline-offset-[5px]";
function FooterLink(t0) {
  const $ = _c(4);
  const {
    to,
    end,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== end || $[2] !== to) {
    t1 = <NavLink to={to} end={end} className={_temp}>{children}</NavLink>;
    $[0] = children;
    $[1] = end;
    $[2] = to;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function _temp(t0) {
  const {
    isActive
  } = t0;
  return cn(LINK, "w-fit", isActive && ACTIVE);
}
export function SiteFooter() {
  const $ = _c(100);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Date().getFullYear();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const year = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("mx-auto flex w-full max-w-[1440px] flex-col gap-4 px-4 py-5 md:px-8", "min-[744px]:flex-row min-[744px]:justify-between min-[744px]:py-[30px]");
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Logo tone="dark" className="py-1 lg:py-1.5" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("footer.tagline", {
      defaultValue: "\u041F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430 \u0434\u043B\u044F \u043F\u0440\u043E\u0434\u0430\u0436\u0438 \u0438\u0437\u0434\u0435\u043B\u0438\u0439 \u0440\u0443\u0447\u043D\u043E\u0439 \u0440\u0430\u0431\u043E\u0442\u044B"
    });
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <div className="flex flex-col gap-0.5 min-[744px]:gap-1.5 lg:gap-1">{t2}<p className={TEXT}>{t3}</p></div>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t) {
    t5 = t("footer.sell", {
      defaultValue: "\u041F\u0440\u043E\u0434\u0430\u0432\u0430\u0442\u044C \u043D\u0430 OSMIX"
    });
    $[7] = t;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <FooterLink to="/sell">{t5}</FooterLink>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t) {
    t7 = t("footer.partners", {
      defaultValue: "\u041F\u0430\u0440\u0442\u043D\u0451\u0440\u0430\u043C"
    });
    $[11] = t;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t7) {
    t8 = <FooterLink to="/partner">{t7}</FooterLink>;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t6 || $[17] !== t8) {
    t9 = <div className="flex flex-col gap-3 min-[744px]:gap-1.5 lg:gap-1">{t4}{t6}{t8}</div>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("footer.rights", {
      defaultValue: "\u0412\u0441\u0435 \u043F\u0440\u0430\u0432\u0430 \u0437\u0430\u0449\u0438\u0449\u0435\u043D\u044B"
    });
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10) {
    t11 = <p className={TEXT}>© {year} OSMIX. {t10}.</p>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex flex-col gap-3 min-[744px]:w-[235px] min-[744px]:shrink-0 min-[744px]:justify-between min-[744px]:gap-6 lg:w-[208px] min-[1200px]:w-[282px]">{t9}{t11}</div>;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t) {
    t13 = t("footer.shop", {
      defaultValue: "\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044F\u043C"
    });
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t) {
    t14 = t("footer.shop", {
      defaultValue: "\u041F\u043E\u043A\u0443\u043F\u0430\u0442\u0435\u043B\u044F\u043C"
    });
    $[28] = t;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t14) {
    t15 = <span className={HEADING}>{t14}</span>;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t) {
    t16 = t("footer.feed", {
      defaultValue: "\u041B\u0435\u043D\u0442\u0430"
    });
    $[32] = t;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t16) {
    t17 = <FooterLink to="/" end={true}>{t16}</FooterLink>;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t) {
    t18 = t("footer.catalog", {
      defaultValue: "\u041A\u0430\u0442\u0430\u043B\u043E\u0433"
    });
    $[36] = t;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t18) {
    t19 = <FooterLink to="/catalog">{t18}</FooterLink>;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t) {
    t20 = t("footer.promos", {
      defaultValue: "\u0410\u043A\u0446\u0438\u0438"
    });
    $[40] = t;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== t20) {
    t21 = <FooterLink to="/promos">{t20}</FooterLink>;
    $[42] = t20;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== t13 || $[45] !== t15 || $[46] !== t17 || $[47] !== t19 || $[48] !== t21) {
    t22 = <nav className="flex flex-col gap-1 min-[744px]:gap-1.5 lg:gap-2" aria-label={t13}>{t15}{t17}{t19}{t21}</nav>;
    $[44] = t13;
    $[45] = t15;
    $[46] = t17;
    $[47] = t19;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t) {
    t23 = t("footer.help", {
      defaultValue: "\u041F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0430"
    });
    $[50] = t;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t) {
    t24 = t("footer.help", {
      defaultValue: "\u041F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0430"
    });
    $[52] = t;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <span className={HEADING}>{t24}</span>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t) {
    t26 = t("footer.delivery", {
      defaultValue: "\u0414\u043E\u0441\u0442\u0430\u0432\u043A\u0430"
    });
    $[56] = t;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] !== t26) {
    t27 = <FooterLink to="/delivery">{t26}</FooterLink>;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== t) {
    t28 = t("footer.payment", {
      defaultValue: "\u041E\u043F\u043B\u0430\u0442\u0430"
    });
    $[60] = t;
    $[61] = t28;
  } else {
    t28 = $[61];
  }
  let t29;
  if ($[62] !== t28) {
    t29 = <FooterLink to="/payment">{t28}</FooterLink>;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== t) {
    t30 = t("footer.returns", {
      defaultValue: "\u0412\u043E\u0437\u0432\u0440\u0430\u0442"
    });
    $[64] = t;
    $[65] = t30;
  } else {
    t30 = $[65];
  }
  let t31;
  if ($[66] !== t30) {
    t31 = <FooterLink to="/returns">{t30}</FooterLink>;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  let t32;
  if ($[68] !== t) {
    t32 = t("footer.contacts", {
      defaultValue: "\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u044B"
    });
    $[68] = t;
    $[69] = t32;
  } else {
    t32 = $[69];
  }
  let t33;
  if ($[70] !== t32) {
    t33 = <FooterLink to="/contacts">{t32}</FooterLink>;
    $[70] = t32;
    $[71] = t33;
  } else {
    t33 = $[71];
  }
  let t34;
  if ($[72] !== t23 || $[73] !== t25 || $[74] !== t27 || $[75] !== t29 || $[76] !== t31 || $[77] !== t33) {
    t34 = <nav className="flex flex-col gap-1 min-[744px]:gap-1.5 lg:gap-2" aria-label={t23}>{t25}{t27}{t29}{t31}{t33}</nav>;
    $[72] = t23;
    $[73] = t25;
    $[74] = t27;
    $[75] = t29;
    $[76] = t31;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  let t35;
  if ($[79] !== t22 || $[80] !== t34) {
    t35 = <div className="flex items-start gap-4 min-[744px]:contents">{t22}{t34}</div>;
    $[79] = t22;
    $[80] = t34;
    $[81] = t35;
  } else {
    t35 = $[81];
  }
  let t36;
  if ($[82] !== t) {
    t36 = t("footer.legal", {
      defaultValue: "\u041F\u0440\u0430\u0432\u043E\u0432\u0430\u044F \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F"
    });
    $[82] = t;
    $[83] = t36;
  } else {
    t36 = $[83];
  }
  let t37;
  if ($[84] !== t36) {
    t37 = <span className={HEADING}>{t36}</span>;
    $[84] = t36;
    $[85] = t37;
  } else {
    t37 = $[85];
  }
  let t38;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <ul className={cn("grid grid-flow-col grid-cols-2 grid-rows-4 gap-x-2 gap-y-1", "min-[744px]:grid-cols-[112px_110px] min-[744px]:gap-x-6 min-[744px]:gap-y-2", "lg:grid-cols-[134px_108px_131px] lg:grid-rows-3")}>{LEGAL_DOCS.map(_temp2)}</ul>;
    $[86] = t38;
  } else {
    t38 = $[86];
  }
  let t39;
  if ($[87] !== t37) {
    t39 = <div className="flex flex-col gap-1.5 lg:gap-2">{t37}{t38}</div>;
    $[87] = t37;
    $[88] = t39;
  } else {
    t39 = $[88];
  }
  let t40;
  if ($[89] !== t35 || $[90] !== t39) {
    t40 = <div className="flex flex-col gap-3 min-[744px]:flex-row min-[744px]:items-start min-[744px]:gap-6 lg:gap-12">{t35}{t39}</div>;
    $[89] = t35;
    $[90] = t39;
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] !== t12 || $[93] !== t40) {
    t41 = <div className={t1}>{t12}{t40}</div>;
    $[92] = t12;
    $[93] = t40;
    $[94] = t41;
  } else {
    t41 = $[94];
  }
  let t42;
  let t43;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = cn(TEXT, "text-[#2E2E2E]/70");
    t43 = [OPERATOR.entity, `ИНН ${OPERATOR.inn}`, `ОГРНИП ${OPERATOR.ogrnip}`, OPERATOR.address, OPERATOR.phone, OPERATOR.supportEmail].filter(Boolean);
    $[95] = t42;
    $[96] = t43;
  } else {
    t42 = $[95];
    t43 = $[96];
  }
  let t44;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <div className="mx-auto flex w-full max-w-[1440px] flex-col gap-3 border-t border-[#EDEDED] px-4 py-4 md:px-8 min-[744px]:flex-row min-[744px]:items-center min-[744px]:justify-between"><p className={t42}>{t43.join(" \xB7 ")}</p><PaymentBadges className="shrink-0" /></div>;
    $[97] = t44;
  } else {
    t44 = $[97];
  }
  let t45;
  if ($[98] !== t41) {
    t45 = <footer className="mt-16 bg-white">{t41}{t44}</footer>;
    $[98] = t41;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  return t45;
}
function _temp2(doc) {
  return <li key={doc.file}><a href={encodeURI(`/docs/b2c/${doc.file}`)} target="_blank" rel="noopener noreferrer" className={cn(LINK, "block")}>{doc.label}</a></li>;
}